/**
 * ChatInput — floating rounded input bar for the Platform Assistant.
 *
 * Features:
 * - Auto-growing textarea
 * - Send on Enter (Shift+Enter for newline)
 * - Disabled state during loading
 * - Rounded pill aesthetic with inset send button
 */
import { useState, useRef, useCallback, KeyboardEvent, ChangeEvent } from 'react'
import { Send, Loader2, Square, Brain } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from '@/types/chatbot'

export interface ChatInputProps {
  /** Callback when message is submitted */
  onSendMessage: (message: string) => void
  /** Callback to abort the in-flight turn. When provided, the send button becomes a Stop button while sending. */
  onStop?: () => void
  /** Whether the input is disabled (e.g., during loading) */
  disabled?: boolean
  /** Whether a message is being sent */
  isSending?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Maximum character limit */
  maxLength?: number
  /** Show character count */
  showCharCount?: boolean
  /**
   * Per-turn reasoning effort. When provided (with {@link onReasoningEffortChange}), the input renders a
   * compact effort selector that the user can change before each turn. Omit both to hide the control.
   */
  reasoningEffort?: ReasoningEffort
  /** Called when the user picks a different reasoning effort. */
  onReasoningEffortChange?: (effort: ReasoningEffort) => void
  /** Additional CSS classes */
  className?: string
}

export function ChatInput({
  onSendMessage,
  onStop,
  disabled = false,
  isSending = false,
  placeholder = 'Ask the assistant anything…',
  maxLength = 4000,
  showCharCount = false,
  reasoningEffort,
  onReasoningEffortChange,
  className,
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canSend = message.trim().length > 0 && !disabled && !isSending

  const handleSubmit = useCallback(() => {
    if (!canSend) return

    const trimmedMessage = message.trim()
    onSendMessage(trimmedMessage)
    setMessage('')

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [canSend, message, onSendMessage])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (maxLength && value.length > maxLength) return

    setMessage(value)

    // Auto-grow textarea
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
  }, [maxLength])

  const charCount = message.length
  const isNearLimit = maxLength && charCount > maxLength * 0.9

  return (
    <div className={cn('bg-background px-4 pb-4 pt-2', className)}>
      {/* Floating bar container */}
      <div className="relative flex items-end gap-2 rounded-2xl border bg-muted/40 shadow-sm px-4 py-2 focus-within:ring-2 focus-within:ring-ring/40 transition-shadow">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSending}
            className={cn(
              'resize-none min-h-[40px] max-h-[200px] pr-2 border-0 bg-transparent shadow-none p-0',
              'focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60'
            )}
            rows={1}
            aria-label="Message input"
          />
          {showCharCount && (
            <div
              className={cn(
                'absolute bottom-1 right-2 text-[10px] text-muted-foreground/50',
                isNearLimit && 'text-destructive'
              )}
              aria-live="polite"
            >
              {charCount}/{maxLength}
            </div>
          )}
        </div>

        {isSending && onStop ? (
          <Button
            onClick={onStop}
            size="icon"
            variant="secondary"
            className="h-9 w-9 shrink-0 rounded-xl transition-transform active:scale-95"
            aria-label="Stop generating"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSend}
            size="icon"
            className="h-9 w-9 shrink-0 rounded-xl transition-transform active:scale-95"
            aria-label={isSending ? 'Sending…' : 'Send message'}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-1.5">
        {reasoningEffort && onReasoningEffortChange ? (
          <Select
            value={reasoningEffort}
            onValueChange={(v) => onReasoningEffortChange(v as ReasoningEffort)}
            disabled={isSending}
          >
            <SelectTrigger
              className="h-7 w-auto gap-1.5 rounded-lg border-muted-foreground/20 bg-transparent px-2 text-[11px] text-muted-foreground shadow-none focus:ring-1"
              aria-label="Reasoning effort"
            >
              <Brain className="h-3.5 w-3.5 opacity-70" />
              {/* Compact trigger: just "Reasoning: {label}", not the rich item content SelectValue would mirror. */}
              <span>
                Reasoning:{' '}
                <span className="font-medium text-foreground">
                  {REASONING_EFFORT_OPTIONS.find((o) => o.value === reasoningEffort)?.label ?? reasoningEffort}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent align="start">
              {REASONING_EFFORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  <span className="font-medium">{opt.label}</span>
                  <span className="ml-2 text-muted-foreground">{opt.hint}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span />
        )}

        <p className="text-[11px] text-muted-foreground/50">
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  )
}
